<template>
  <Dialog @submit="onSubmit" :title="title">
    <div class="billing-modal">
      <div v-if="showSpinner" class="spinner-wrap">
        <spinner />
      </div>
      <form @submit.prevent="onSubmit">
        <div class="form-group">
          <label for="card-number">Card Number*</label>
          <div id="card-number"
               class="stripe-element"
               :class="{'is-invalid': errorMessage.cardNumberError}"></div>
          <small v-show="errorMessage.cardNumberError" class="invalid-feedback">
            {{errorMessage.cardNumberError}}
          </small>
        </div>
        <div class="row">
          <div class="col-md-4 col-sm-12">
            <div class="form-group">
              <label for="card-expiry">Expiration*</label>
              <div id="card-expiry"
                   class="stripe-element"
                   :class="{'is-invalid': errorMessage.cardExpiryError}"></div>
              <small v-show="errorMessage.cardExpiryError" class="invalid-feedback">
                {{errorMessage.cardExpiryError}}
              </small>
            </div>
          </div>
          <div class="col-md-4 col-sm-12">
            <div class="form-group">
              <label for="card-cvc">CVC*</label>
              <div id="card-cvc"
                   class="stripe-element"
                   :class="{'is-invalid': errorMessage.cardCVCError}"></div>
              <small v-show="errorMessage.cardCVCError" class="invalid-feedback">
                {{errorMessage.cardCVCError}}
              </small>
            </div>
          </div>
          <div class="col-md-4 col-sm-12">
            <div class="form-group">
              <label for="card-zip">Zip/Postal*</label>
              <input id="card-zip"
                     class="form-control"
                     type="text"
                     name="cardZip"
                     :class="{'is-invalid': errors.has('cardZip') || validMaxLength(maxZip)}"
                     v-model.trim="cardZip"
                     v-validate="'required'"
                     placeholder="9210"/>
              <small v-show="errors.has('cardZip')" class="invalid-feedback">
                {{ errors.first('cardZip') }}
              </small>
              <small v-show="validMaxLength(maxZip)" class="invalid-feedback">
                Max length can't be then {{maxZip}}
              </small>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label for="card-name">Cardholder name*</label>
          <input id="card-name"
                 class="form-control"
                 type="text"
                 name="cardName"
                 :class="{'is-invalid': errors.has('cardName')}"
                 v-model.trim="cardName"
                 v-validate="'required'"
                 placeholder="Cardholder full name"/>
          <small v-show="errors.has('cardName')" class="invalid-feedback">
            {{ errors.first('cardName') }}
          </small>
        </div>
        <div class="form-group">
          <label for="card-email">Cardholder email*</label>
          <input id="card-email"
                 class="form-control"
                 type="text"
                 name="cardEmail"
                 :class="{'is-invalid': errors.has('cardEmail')}"
                 v-model.trim="cardEmail"
                 v-validate="'required'"
                 placeholder="Cardholder email"/>
          <small v-show="errors.has('cardEmail')" class="invalid-feedback">
            {{ errors.first('cardEmail') }}
          </small>
        </div>
      </form>
    </div>
  </Dialog>
</template>

<script>
import Switches from 'vue-switches';
import Dialog from '@/components/Dialog';
import ModalMixin from '@/mixins/ModalMixin';
import Spinner from '@/components/Spinner';

import NEW_CARD from '@/graphql/Billing/NewCard.gql';

/* eslint-disable no-undef */
const stripe = Stripe(process.env.STRIPE_PUBLISHABLE_KEY);

const style = {
  base: {
    border: '1px solid #d8d8d8',
    borderRadius: '1px',
    color: '#54667a',
    fontSize: '16px',
    lineHeight: '26px',

    '::placeholder': {
      color: 'lightgray',
    },
  },
  invalid: {
    color: '#e25950',
  },
};

const elementClasses = {
  focus: 'focused',
  empty: 'empty',
  invalid: 'invalid',
};

const elementOptions = {
  style,
  classes: elementClasses,
};

export default {
  name: 'add-card-component',
  mixins: [ModalMixin],
  components: {
    Dialog,
    Switches,
    Spinner,
  },
  data() {
    return {
      id: this.$route.params.id,
      userEmail: this.$store.getters.getAuthUser.email,
      zip: '',
      maxZip: 6,
      cardName: '',
      cardEmail: '',
      errorMessage: {},
      showSpinner: 0,
    };
  },
  computed: {
    title() {
      return 'Add Payment Card';
    },
    cardZip: {
      get() {
        return this.zip || '';
      },
      set(val) {
        this.zip = val;
      },
    },
  },
  mounted() {
    const elements = stripe.elements();

    this.cardNumber = elements.create('cardNumber', elementOptions);
    this.cardNumber.mount('#card-number');

    this.cardExpiry = elements.create('cardExpiry', elementOptions);
    this.cardExpiry.mount('#card-expiry');

    this.cardCvc = elements.create('cardCvc', elementOptions);
    this.cardCvc.mount('#card-cvc');
  },
  methods: {
    validMaxLength(number) {
      return this.cardZip.length > number;
    },
    async onSubmit() {
      if (this.showSpinner) {
        return null;
      }
      this.showSpinner = true;
      await this.sent();
    },
    async sent() {
      const options = {
        address_zip: this.cardZip,
      };

      const token = await stripe.createToken(this.cardNumber, options);
      const isValid = await this.$validator.validateAll();

      try {
        this.cardHandler(token);
        if (!isValid) {
          return null;
        }
        this.createCard(token.token.id);
      } catch (error) {
        /* eslint-disable no-console */
        console.error('Error', error);
      }
    },
    cardHandler({ error }) {
      if (error) {
        this.errorMessage = {};
        if (error.code === 'incomplete_number' || error.code === 'invalid_number') {
          this.errorMessage.cardNumberError = error.message;
        } else if (error.code === 'incomplete_cvc') {
          this.errorMessage.cardCVCError = error.message;
        } else if (error.code === 'incomplete_expiry') {
          this.errorMessage.cardExpiryError = error.message;
        }
      } else {
        this.errorMessage = {};
      }
    },
    createCard(token) {
      this.$apollo
        .mutate({
          mutation: NEW_CARD,
          variables: {
            id: this.id,
            input: {
              token,
              customerEmail: this.userEmail,
              holderEmail: this.cardEmail,
              cardholder: this.cardName,
            },
          },
          refetchQueries: ({ data }) => {
            const response = data.addCard;
            return response && response.success ? ['billing'] : [];
          },
        })
        .then(() => {
          this.$notify({
            group: 'success',
            type: 'success',
            text: 'Card was created',
          });

          this.close();
        })
        .catch(e => {
          this.$notify({
            group: 'error',
            type: 'error',
            text: e.message,
          });
        })
        .finally(() => {
          this.showSpinner = false;
        });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.form-group {
  margin-bottom: 12px;
}
.vue-switcher {
  margin-left: 20px;
  margin-bottom: 0;
  top: 2px;
}

.billing-modal {
  position: relative;
}

.spinner-wrap {
  left: -1.25em;
  top: -1.25em;
  right: -1.25em;
  bottom: -2em;
  background: rgba(222, 222, 222, 0.2);
  position: absolute;
  z-index: 100;
}

.stripe-element {
  color: #fff;
  min-height: 38px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 6px 12px;

  &.is-invalid {
    border-color: #dc3545;

    ~ .invalid-feedback {
      display: block;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modals/AddCardModal.vue